import React, { useCallback, useEffect, useState } from "react";

import { type DecisionRow } from "../../../types";
import { type IncreaseValues } from "../types";

import { useDecisionSubmit, useSubmitDecisions } from "../../../../../api/hooks";

export const useDecisionValidation = (args: {
	cycleId: string | undefined;
	userID: string | undefined;
	groupDecisionRowsByManagerId: Record<string, DecisionRow[]>;
	setDraftOverrides: React.Dispatch<React.SetStateAction<IncreaseValues>>;
}) => {
	const { cycleId, userID, groupDecisionRowsByManagerId, setDraftOverrides } = args;

	const [groupCannotBeSubmitted, setGroupCannotBeSubmitted] = useState<string | null>(null);
	const [refreshingGroupIds, setRefreshingGroupIds] = useState<Record<string, boolean>>({});
	const { mutate: submitDecisions } = useSubmitDecisions();

	useEffect(() => {
		if (groupCannotBeSubmitted == null) {
			return;
		}
		const rows = groupDecisionRowsByManagerId[groupCannotBeSubmitted] ?? [];
		const hasNullIncreases = rows.some(
			(row) => row.proposed_increase_amount === null || row.proposed_increase_percent === null
		);
		if (!hasNullIncreases) {
			setGroupCannotBeSubmitted(null);
		}
	}, [groupCannotBeSubmitted, groupDecisionRowsByManagerId]);

	const handleSubmitDecisions = useDecisionSubmit({
		cycleId,
		userID,
		groupDecisionRowsByManagerId,
		submitDecisions,
		setGroupCannotBeSubmitted,
		setRefreshingGroupIds,
		setDraftOverrides,
	});

	const validateSubmitDecisions = useCallback(
		(managerId: string) => {
			if (!managerId) {
				return;
			}
			const groupRows = groupDecisionRowsByManagerId[managerId] ?? [];
			if (!groupRows.length || !userID || !cycleId) return;
			const rowsWithNullIncreases = groupRows.filter(
				(row) => row.proposed_increase_amount === null || row.proposed_increase_percent === null
			);
			if (rowsWithNullIncreases.length) {
				setGroupCannotBeSubmitted(managerId);
				return;
			}
			setGroupCannotBeSubmitted(null);
			handleSubmitDecisions(managerId);
		},
		[cycleId, groupDecisionRowsByManagerId, handleSubmitDecisions, userID]
	);

	return { groupCannotBeSubmitted, refreshingGroupIds, validateSubmitDecisions };
};
